import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch } from "../store";
import { itemActions } from "../slices/item";
import { getErrorMessage } from "../../utils/errorUtils";

import { ItemRequestDto } from "../../shared/dtos/item/ItemRequestDto";
import { ItemResponseDto } from "../../shared/dtos/item/ItemResponseDto";
import { ItemImportReportDto } from "../../shared/dtos/item/ItemImportReportDto";
import { ItemSyncReportDto } from "../../shared/dtos/item/ItemSyncReportDto";
import { ItemQueryDto } from "../../shared/dtos/item/ItemQueryDto";
import { UpdateItemPayload } from "../../shared/interfaces/item/UpdateItemPayload";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { PaginatedResponse } from "../../shared/dtos/common/PaginatedResponse";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";

export const getItems =
  (params?: ItemQueryDto) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(itemActions.setLoadingFetchAll());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<PaginatedResponse<ItemResponseDto>>
      >("/items", {
        headers: { requiresAuth: true },
        params,
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(itemActions.setItems(data.data.content));
        dispatch(itemActions.setPagination(data.data.pagination));
      }
    } catch (error) {
      dispatch(itemActions.setErrorFetchAll(getErrorMessage(error)));
    }
  };

export const getItemById =
  (id: string) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(itemActions.setLoadingFetchOne());
    try {
      const { data } = await axiosInstance.get<ApiResponseDto<ItemResponseDto>>(
        `/items/${id}`,
        {
          headers: { requiresAuth: true },
        },
      );
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(itemActions.setItem(data.data));
      }
    } catch (error) {
      dispatch(itemActions.setErrorFetchOne(getErrorMessage(error)));
    }
  };

export const createItem =
  (values: ItemRequestDto) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(itemActions.setLoadingCreate());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<ItemResponseDto>
      >("/items", values, {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Item created successfully!");
        dispatch(itemActions.createSuccess(data.data));
        dispatch(
          itemActions.setServerStatus({
            operation: "create",
            status: data.statusCode || 201,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(itemActions.setErrorCreate(message));
    }
  };

export const downloadItemImportTemplate = () => async (): Promise<void> => {
  try {
    const response = await axiosInstance.get("/items/import-template", {
      headers: { requiresAuth: true },
      responseType: "blob",
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "item_import_template.xlsx");
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    toast.error("Failed to download the import template.");
  }
};

export const importItems =
  (file: File) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(itemActions.setLoadingImport());
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await axiosInstance.post<
        ApiResponseDto<ItemImportReportDto>
      >("/items/import", formData, {
        headers: { requiresAuth: true, "Content-Type": "multipart/form-data" },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(itemActions.importItemsSuccess(data.data));
        dispatch(
          itemActions.setServerStatus({
            operation: "import",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      dispatch(itemActions.setErrorImport(getErrorMessage(error)));
    }
  };

export const syncItemsToBranch =
  () =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(itemActions.setLoadingSync());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<ItemSyncReportDto>
      >("/items/sync", {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Sync completed successfully!");
        dispatch(itemActions.syncItemsSuccess(data.data));
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(itemActions.setErrorSync(message));
    }
  };

export const updateItem =
  (payload: UpdateItemPayload) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(itemActions.setLoadingUpdate());
    try {
      const { data } = await axiosInstance.put<ApiResponseDto<ItemResponseDto>>(
        `/items/${payload.id}`,
        payload.values,
        {
          headers: { requiresAuth: true },
        },
      );
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Item updated successfully!");
        dispatch(itemActions.updateSuccess(data.data));
        dispatch(
          itemActions.setServerStatus({
            operation: "update",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(itemActions.setErrorUpdate(message));
    }
  };

export const archiveItem =
  (id: string) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(itemActions.setLoadingArchive());
    try {
      const { data } = await axiosInstance.patch<ApiResponseDto<void>>(
        `/items/archive/${id}`,
        null,
        {
          headers: { requiresAuth: true },
        },
      );
      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success(data.message || "Archive status updated!");
        dispatch(itemActions.archiveItemSuccess(id));
        dispatch(
          itemActions.setServerStatus({
            operation: "archive",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      dispatch(itemActions.setErrorArchive(getErrorMessage(error)));
    }
  };

export const deleteItem =
  (id: string) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(itemActions.setLoadingDelete());
    try {
      const { data } = await axiosInstance.delete<ApiResponseDto<void>>(
        `/items/${id}`,
        {
          headers: { requiresAuth: true },
        },
      );
      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success(data.message || "Item deleted successfully!");
        dispatch(itemActions.deleteSuccess(id));
        dispatch(
          itemActions.setServerStatus({
            operation: "delete",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      dispatch(itemActions.setErrorDelete(getErrorMessage(error)));
    }
  };

export const resetItemServerStatus = () => (dispatch: AppDispatch) =>
  dispatch(itemActions.setServerStatus(null));
